/**
 * TSS cart page behaviours (SSI layout). Layers on top of Dawn's cart.js:
 *   <cart-qty-select>  — Qty pill; routes to Dawn's updateQuantity.
 *   <cart-size-select> — Size pill; swaps the line's variant (remove + add).
 *   <cart-discount>    — Apply/remove coupon codes via /cart/update.js.
 *   <cart-page>        — Remove / Move-to-wishlist confirmation modal, and the
 *                        size picker for multi-variant recommendation tiles.
 *
 * These live inside `.js-contents` (re-rendered on every cart update) except
 * <cart-page>, which wraps everything and owns the persistent modal.
 *
 * Loaded as a classic deferred script (not a module): it sits in the section
 * body, which precedes the importmap (snippets/scripts.liquid) — a body-level
 * module tag there would make the browser reject that importmap. Wrapped in an
 * IIFE so `routes`/class names never leak into the global lexical scope Dawn's
 * classic scripts share. Cross-component signal is a plain `cart:update`
 * CustomEvent (same name as @theme/events ThemeEvents.cartUpdate).
 */

(() => {
  const routes = window.routes || {};

  function postJson(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    };
  }

  // DOM targets to re-render after a cart mutation, and the selector inside each
  // returned section HTML whose innerHTML replaces the live target.
  function cartSectionTargets() {
    const targets = [];
    const mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) {
      targets.push({ el: mainItems, key: mainItems.dataset.id, selector: '.js-contents' });
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) targets.push({ el: bubble, key: 'cart-icon-bubble', selector: '.shopify-section' });
    return targets;
  }

  /**
   * POST a cart mutation with bundled section rendering: the cart endpoints
   * (add/change/update) return updated HTML for the requested sections in the
   * same response, so no follow-up Section Rendering fetch is needed.
   * @returns {Promise<Object>} cart JSON with a `sections` map
   */
  async function mutateCart(url, body) {
    const sections = cartSectionTargets().map((t) => t.key);
    const res = await fetch(url, postJson({ ...body, sections: sections.join(','), sections_url: window.location.pathname }));
    const cart = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(cart.description || cart.message || 'Cart request failed');
    return cart;
  }

  /** Swap the returned section HTML into the live DOM (mirrors Dawn's re-render). */
  function applyCartSections(sections) {
    if (!sections) return;
    for (const { el, key, selector } of cartSectionTargets()) {
      const html = sections[key];
      if (html == null) continue;
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (parsed) (el.querySelector(selector) || el).innerHTML = parsed.innerHTML;
    }
  }

  /** Let the rest of the theme (drawer, etc.) know the cart changed. */
  function notifyCartUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'cart-page', data: { source: 'cart-page' } }
      })
    );
  }

  /* -------------------------------------------------- Qty pill (Dawn flow) -- */
  class CartQtySelect extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('select');
      if (!select) return;
      select.addEventListener('change', (event) => {
        // Prevent Dawn's generic <cart-items> change handler (which expects a
        // numeric input with min/max/step) from validating this <select>.
        event.stopPropagation();

        const quantity = parseInt(select.value, 10);
        const line = select.dataset.index;
        const label = this.querySelector('.cart-field__text strong');
        if (label) label.textContent = select.value;

        const cartItems = this.closest('cart-items');
        if (cartItems) cartItems.updateQuantity(line, quantity, event);
      });
    }
  }

  /* --------------------------------------------------- Size pill (variant) -- */
  class CartSizeSelect extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('select');
      if (!select || select.disabled) return;
      this.currentValue = select.value;
      select.addEventListener('change', (event) => {
        // Don't let the size change bubble to Dawn's <cart-items> change handler,
        // which would treat the variant id as a quantity.
        event.stopPropagation();
        this.swap(select);
      });
    }

    async swap(select) {
      const newId = select.value;
      if (newId === this.currentValue) return;

      const oldKey = select.dataset.lineKey;
      const quantity = parseInt(select.dataset.quantity, 10) || 1;

      this.setBusy(true);
      try {
        // Single atomic call: /cart/update.js sets the old line's quantity to 0
        // and adds the new variant in one request — update.js adds a variant that
        // isn't in the cart yet, keyed by variant id. Bundled sections come back
        // in the same response. (add.js was avoided here: it caps to available
        // stock and warns "Only N added", which we don't want mid-swap.)
        const cart = await mutateCart(routes.cart_update_url, {
          updates: { [oldKey]: 0, [newId]: quantity }
        });
        applyCartSections(cart.sections);
        notifyCartUpdate(cart);
      } catch (e) {
        // update.js is atomic, so a failure leaves the cart unchanged.
        console.error('[cart-page] variant swap failed', e);
        select.value = this.currentValue; // revert the control
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(state) {
      const spinner = this.closest('.cart-item')?.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !state);
      const items = document.getElementById('main-cart-items');
      if (items) items.classList.toggle('cart__items--disabled', state);
    }
  }

  /* ------------------------------------------------------- Coupon / discount -- */
  class CartDiscount extends HTMLElement {
    connectedCallback() {
      const header = this.querySelector('.cart-coupon__header');
      const body = this.querySelector('.cart-coupon__body');
      if (header && body) {
        header.addEventListener('click', () => {
          const open = header.getAttribute('aria-expanded') === 'true';
          header.setAttribute('aria-expanded', String(!open));
          body.hidden = open;
        });
      }

      this.querySelector('[data-coupon-apply]')?.addEventListener('click', () => this.apply());
      this.querySelector('[data-coupon-input]')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.apply();
        }
      });
      // Delegated — survives the re-render that follows every cart update.
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-remove-code]');
        if (trigger && this.contains(trigger)) this.remove(trigger.dataset.removeCode);
      });
    }

    get codes() {
      return (this.dataset.codes || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
    }

    async apply() {
      const input = this.querySelector('[data-coupon-input]');
      const code = (input?.value || '').trim();
      if (!code) return;
      const next = Array.from(new Set([...this.codes, code]));
      await this.update(next, code);
    }

    async remove(code) {
      const next = this.codes.filter((c) => c.toLowerCase() !== code.toLowerCase());
      await this.update(next, null);
    }

    async update(codes, addedCode) {
      this.setError('');
      let cart;
      try {
        // /cart/update.js `discount` replaces the whole code set ('' clears it).
        cart = await mutateCart(routes.cart_update_url, { discount: codes.join(',') });
      } catch (e) {
        console.error('[cart-page] discount update failed', e);
        this.setError(window.cartPageStrings?.couponError || 'Unable to apply coupon.');
        return;
      }

      // Shopify stores an unknown/ineligible code with applicable:false and leaves
      // totals untouched. Show the error and skip the re-render (which would wipe it).
      if (addedCode) {
        const entry = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === addedCode.toLowerCase());
        if (!entry || !entry.applicable) {
          this.setError(window.cartPageStrings?.couponError || 'Unable to apply coupon.');
          return;
        }
      }

      applyCartSections(cart.sections);
      notifyCartUpdate(cart);
    }

    setError(message) {
      const el = this.querySelector('[data-coupon-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }
  }

  /* ----------------------------------------------- Page controller + modal -- */
  class CartPage extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-cart-modal]');
      this.pending = null;

      // Lines not sold in this market block checkout (sections/main-cart-items.liquid
      // marks the buttons); the prompt removes them. Delegated, as the section re-renders.
      this.addEventListener('click', (event) => this.handleMarketClick(event), true);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeMarketModal();
      });

      // Remove / Move-to-wishlist triggers (delegated — survives re-renders).
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-cart-action]');
        if (trigger && this.contains(trigger)) {
          event.preventDefault();
          this.openModal(trigger);
        }
      });

      if (this.modal) {
        this.modal.querySelectorAll('[data-modal-close]').forEach((el) => el.addEventListener('click', () => this.closeModal()));
        this.modal.querySelector('[data-modal-confirm]')?.addEventListener('click', () => this.confirm());
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
        });
      }

      this.sizeModal = this.querySelector('[data-size-modal]');
      if (this.sizeModal) {
        this.sizeModal.querySelectorAll('[data-size-close]').forEach((el) => el.addEventListener('click', () => this.closeSizePicker()));
        this.sizeModal.querySelector('[data-size-add]')?.addEventListener('click', () => this.addPickedSize());
        this.sizeModal.querySelector('[data-size-options]')?.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-variant-id]');
          if (chip && !chip.disabled) this.pickSize(chip);
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.sizeModal.hidden) this.closeSizePicker();
        });
      }
    }

    /* ---------------------------------------------------- Size picker -- */

    /** @param {HTMLElement} trigger - A tile's `[data-add-now-pick]` button. */
    openSizePicker(trigger) {
      if (!this.sizeModal) return;
      let variants = [];
      try {
        variants = JSON.parse(trigger.dataset.variants || '[]');
      } catch (error) {
        console.error('[cart-page] bad variant data', error);
        return;
      }

      this.sizeSource = trigger;
      this.sizeModal.querySelector('[data-size-title]').textContent = trigger.dataset.title || '';
      this.sizeModal.querySelector('[data-size-price]').textContent = trigger.dataset.price || '';
      const thumb = this.sizeModal.querySelector('[data-size-thumb]');
      if (thumb) thumb.src = trigger.dataset.thumb || '';

      const options = this.sizeModal.querySelector('[data-size-options]');
      options.replaceChildren(
        ...variants.map((variant) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'cart-size-modal__size';
          chip.setAttribute('role', 'radio');
          chip.setAttribute('aria-checked', 'false');
          chip.dataset.variantId = variant.id;
          chip.textContent = variant.label;
          chip.disabled = !variant.available;
          return chip;
        })
      );

      this.setSizeError('');
      this.sizeModal.hidden = false;
      document.body.style.overflow = 'hidden';
      (options.querySelector('button:not(:disabled)') || this.sizeModal.querySelector('[data-size-close]'))?.focus();
    }

    closeSizePicker() {
      if (!this.sizeModal) return;
      this.sizeModal.hidden = true;
      document.body.style.overflow = '';
      this.sizeSource?.focus();
      this.sizeSource = null;
    }

    pickSize(chip) {
      this.sizeModal.querySelectorAll('[data-variant-id]').forEach((other) => {
        other.setAttribute('aria-checked', String(other === chip));
      });
      this.setSizeError('');
    }

    async addPickedSize() {
      const picked = this.sizeModal.querySelector('[data-variant-id][aria-checked="true"]');
      const strings = window.cartPageStrings || {};
      if (!picked) {
        this.setSizeError(strings.sizeError || 'Please select a size.');
        return;
      }

      const addButton = this.sizeModal.querySelector('[data-size-add]');
      addButton.disabled = true;
      // Same name as ThemeEvents.cartRecommendationAdd, so tracking can attribute
      // the add to the tile it came from.
      document.dispatchEvent(
        new CustomEvent('cart:recommendation-add', {
          detail: { trigger: this.sizeSource, variantLabel: picked.textContent }
        })
      );

      try {
        const cart = await mutateCart(routes.cart_add_url, { id: Number(picked.dataset.variantId), quantity: 1 });
        applyCartSections(cart.sections);
        notifyCartUpdate(cart);
        this.closeSizePicker();
      } catch (error) {
        console.error('[cart-page] size add failed', error);
        this.setSizeError(error.message || strings.addError || 'Couldn’t add this item.');
      } finally {
        addButton.disabled = false;
      }
    }

    setSizeError(message) {
      const error = this.sizeModal.querySelector('[data-size-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    handleMarketClick(event) {
      if (event.target.closest('[name="checkout"][data-market-blocked]')) {
        event.preventDefault();
        event.stopPropagation();
        this.openMarketModal();
      } else if (event.target.closest('[data-market-close]')) {
        this.closeMarketModal();
      } else {
        const removeButton = event.target.closest('[data-market-remove]');
        if (removeButton) this.removeUnavailableLines(removeButton);
      }
    }

    openMarketModal() {
      const modal = this.querySelector('[data-market-modal]');
      if (!modal) return;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      modal.querySelector('[data-market-remove]')?.focus();
    }

    closeMarketModal() {
      const modal = this.querySelector('[data-market-modal]');
      if (!modal || modal.hidden || modal.classList.contains('is-busy')) return;
      modal.hidden = true;
      document.body.style.overflow = '';
    }

    /** Drop every line not sold in this market in one update, then reload the cart. */
    async removeUnavailableLines(button) {
      const keys = (button.dataset.lineKeys || '').split(',').filter(Boolean);
      if (keys.length === 0) return;
      const modal = button.closest('[data-market-modal]');
      const setBusy = (busy) => {
        button.disabled = busy;
        button.classList.toggle('is-loading', busy);
        button.setAttribute('aria-busy', String(busy));
        modal?.classList.toggle('is-busy', busy);
      };
      setBusy(true);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: Object.fromEntries(keys.map((key) => [key, 0])) })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        window.location.reload();
      } catch (error) {
        console.error('[cart-page] could not remove unavailable items', error);
        setBusy(false);
      }
    }

    openModal(trigger) {
      if (!this.modal) return;
      const action = trigger.dataset.cartAction; // 'remove' | 'wishlist'
      this.pending = {
        action,
        line: trigger.dataset.lineIndex,
        wishlist: {
          productId: trigger.dataset.productId,
          variantId: trigger.dataset.variantId,
          url: trigger.dataset.productUrl
        }
      };

      const strings = window.cartPageStrings || {};
      const isWishlist = action === 'wishlist';
      this.setText('[data-modal-title]', isWishlist ? strings.wishlistTitle : strings.removeTitle);
      this.setText('[data-modal-body]', isWishlist ? strings.wishlistBody : strings.removeBody);

      const thumb = this.modal.querySelector('[data-modal-thumb]');
      if (thumb) thumb.src = trigger.dataset.thumb || '';

      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      this.modal.querySelector('[data-modal-confirm]')?.focus();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.pending = null;
      document.body.style.overflow = '';
    }

    confirm() {
      if (!this.pending) return;
      const { action, line, wishlist } = this.pending;
      const cartItems = this.querySelector('cart-items');
      const dropLine = () => {
        if (!cartItems) return;
        // Same name as ThemeEvents.cartLineRemove — analytics reports it.
        document.dispatchEvent(new CustomEvent('cart:line-remove', { detail: { line, reason: action } }));
        cartItems.updateQuantity(line, 0, {});
      };

      // Move-to-wishlist: save first, drop the line only once saved (a failed
      // save keeps it in the cart). Remove just drops it.
      if (action === 'wishlist' && wishlist?.variantId) {
        this.addToWishlist(wishlist, dropLine);
      } else {
        dropLine();
      }
      this.closeModal();
    }

    /**
     * Save a line to the local wishlist (assets/wishlist.js), then run `onSaved`.
     * @param {{productId:string, variantId:string, url:string}} item
     * @param {Function} onSaved
     */
    async addToWishlist(item, onSaved) {
      try {
        const { wishlist, handleFromUrl } = await import('@theme/wishlist');
        const saved = wishlist.add({ productId: item.productId, variantId: item.variantId, handle: handleFromUrl(item.url) });
        if (saved && onSaved) onSaved();
      } catch (error) {
        console.error('[cart-page] wishlist add failed', error);
      }
    }

    setText(selector, value) {
      const el = this.modal.querySelector(selector);
      if (el) el.textContent = value || '';
    }
  }

  /* ------------------------------------ "Add now" on recommendations tiles -- */
  // Delegated: recommendation tiles are injected async by <product-recommendations>.
  // Multi-variant tiles open the size picker; single-variant ones add straight away.
  document.addEventListener('click', (event) => {
    const pick = event.target.closest('[data-add-now-pick]');
    if (!pick) return;
    event.preventDefault();
    pick.closest('cart-page')?.openSizePicker(pick);
  });

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-add-now]');
    if (!btn) return;
    event.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    try {
      const cart = await mutateCart(routes.cart_add_url, { id: Number(variantId), quantity: 1 });
      applyCartSections(cart.sections);
      notifyCartUpdate(cart);
    } catch (e) {
      console.error('[cart-page] add-now failed', e);
    } finally {
      btn.disabled = false;
    }
  });

  /* ---------------------------- "View details" in the mobile sticky bar ----- */
  // Scrolls the billing breakdown into view (it renders inline further down).
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-view-details]')) return;
    event.preventDefault();
    document.getElementById('CartBillingDetails')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  if (!customElements.get('cart-qty-select')) customElements.define('cart-qty-select', CartQtySelect);
  if (!customElements.get('cart-size-select')) customElements.define('cart-size-select', CartSizeSelect);
  if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount);
  if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);
})();
